import { createFileRoute } from "@tanstack/react-router"
import {
  Container,
  Title,
  Text,
  Stack,
  Box,
  Paper,
  Group,
  Badge,
  Tabs,
  Select,
  TextInput,
  Card,
  Divider,
  Button,
  Alert,
  useMantineColorScheme,
} from "@mantine/core"
import {
  IconNews,
  IconSearch,
  IconAlertTriangle,
  IconExternalLink,
  IconShieldOff,
  IconFileText,
} from "@tabler/icons-react"
import { useState, useMemo } from "react"
import { newsArticles, getCategoryLabel, getCategoryColor, type NewsArticle } from "@/data/news-data"
import { TransparencyBadge, TransparencyWarning } from "@/components/ui/TransparencyBadge"
import { SEO } from "@/components/seo/SEO"
import { StructuredData, createNewsArticleSchema, createCollectionPageSchema } from "@/components/seo/StructuredData"
import { PageCTASection } from "@/components/ui/PageCTASection"

export const Route = createFileRoute("/news")({
  component: NewsPage,
})

function NewsPage() {
  const { colorScheme } = useMantineColorScheme()
  const [activeTab, setActiveTab] = useState<string>("all")
  const [searchTerm, setSearchTerm] = useState("")
  const [categoryFilter, setCategoryFilter] = useState<string>("all")

  const filteredArticles = useMemo(() => {
    const search = searchTerm.toLowerCase()

    return newsArticles
      .filter((article) => {
        // Filter by category
        if (categoryFilter !== "all" && article.category !== categoryFilter) return false

        // Filter by transparency level (tab)
        if (activeTab === "manipulated" && article.transparencyLevel !== "manipulated") return false
        if (activeTab === "authentic" && (!article.transparencyLevel || article.transparencyLevel === "manipulated"))
          return false

        // Filter by search term
        if (
          searchTerm &&
          !article.headline.toLowerCase().includes(search) &&
          !article.summary.toLowerCase().includes(search)
        )
          return false

        return true
      })
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
  }, [activeTab, searchTerm, categoryFilter])

  // Create CollectionPage schema for the news page
  const collectionPageSchema = useMemo(
    () =>
      createCollectionPageSchema({
        name: "Epstein Case News & Updates",
        description:
          "Latest news, document releases, and developments in the Jeffrey Epstein case with transparency warnings for government-manipulated content.",
        url: "https://rtefn.com/news",
        numberOfItems: newsArticles.length,
      }),
    [],
  )

  // Create individual NewsArticle schemas for filtered articles
  const newsSchemas = useMemo(
    () => filteredArticles.slice(0, 20).map((article) => createNewsArticleSchema(article)),
    [filteredArticles],
  )

  const renderArticle = (article: NewsArticle) => (
    <Card
      key={article.id}
      shadow="lg"
      radius="xl"
      p="xl"
      style={{
        background: colorScheme === "dark" ? "rgba(255, 255, 255, 0.05)" : "rgba(255, 255, 255, 0.9)",
        backdropFilter: "blur(20px)",
        border: colorScheme === "dark" ? "1px solid rgba(255, 255, 255, 0.1)" : "1px solid rgba(0, 0, 0, 0.05)",
      }}
    >
      {/* Article Header */}
      <Group justify="space-between" mb="md" align="flex-start">
        <Group gap="xs">
          <Badge color={getCategoryColor(article.category)} variant="light">
            {getCategoryLabel(article.category)}
          </Badge>
          {article.transparencyLevel && (
            <TransparencyBadge level={article.transparencyLevel} details={article.manipulationWarning} />
          )}
          {article.percentagePreviouslyPublic && article.newInfoPercentage && (
            <TransparencyWarning percentage={article.percentagePreviouslyPublic} newInfo={article.newInfoPercentage} />
          )}
          {article.isBreaking && (
            <Badge color="red" variant="filled" size="sm">
              BREAKING
            </Badge>
          )}
        </Group>
        <Text size="sm" c="dimmed">
          {new Date(article.date).toLocaleDateString("en-US", {
            year: "numeric",
            month: "long",
            day: "numeric",
          })}
        </Text>
      </Group>

      {/* Article Content */}
      <Title order={3} mb="sm" style={{ color: colorScheme === "dark" ? "#ffffff" : "#1a1a1a" }}>
        {article.headline}
      </Title>

      <Text
        mb="lg"
        style={{
          color: colorScheme === "dark" ? "rgba(255, 255, 255, 0.8)" : "#64748b",
          lineHeight: 1.6,
        }}
      >
        {article.summary}
      </Text>

      {/* Manipulation Warning */}
      {article.manipulationWarning && (
        <Alert icon={<IconShieldOff size={16} />} title="TRANSPARENCY WARNING" color="red" variant="light" mb="md">
          <Text size="sm">{article.manipulationWarning}</Text>
        </Alert>
      )}

      {/* Article Details */}
      <Divider mb="md" color={colorScheme === "dark" ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.1)"} />

      <Stack gap="xs" mb="lg">
        {article.details.map((detail, index) => (
          <Group key={index} gap="sm">
            <Text size="sm" c="dimmed">
              •
            </Text>
            <Text size="sm" c="dimmed">
              {detail}
            </Text>
          </Group>
        ))}
      </Stack>

      {/* Source Section - Enhanced Visibility */}
      {article.sourceUrl && (
        <>
          <Divider mb="md" color={colorScheme === "dark" ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.1)"} />
          <Paper
            p="md"
            radius="md"
            style={{
              background: colorScheme === "dark" ? "rgba(59, 130, 246, 0.08)" : "rgba(59, 130, 246, 0.03)",
              border: "1px solid rgba(59, 130, 246, 0.2)",
            }}
          >
            <Group justify="space-between" align="center">
              <Group gap="sm">
                <IconFileText size={16} style={{ color: "#3b82f6" }} />
                <div>
                  <Text size="xs" c="dimmed" mb={4}>
                    Original Source
                  </Text>
                  <Text size="sm" fw={500} style={{ color: "#3b82f6" }}>
                    {article.sourceUrl.includes("courtlistener.com")
                      ? "CourtListener (Tier 1)"
                      : article.sourceUrl.includes("oversight.house.gov")
                        ? "House Oversight Committee (Tier 1)"
                        : article.sourceUrl.includes("cbsnews.com") ||
                            article.sourceUrl.includes("miamiherald.com") ||
                            article.sourceUrl.includes("bbc.co")
                          ? "Established News Organization (Tier 2)"
                          : "View Source"}
                  </Text>
                </div>
              </Group>
              <Button
                component="a"
                href={article.sourceUrl}
                target="_blank"
                rel="noopener noreferrer"
                variant="light"
                size="sm"
                rightSection={<IconExternalLink size={14} />}
                color="blue"
              >
                View Source
              </Button>
            </Group>
          </Paper>
        </>
      )}
    </Card>
  )

  return (
    <>
      <SEO
        title="Latest News & Updates"
        description="Critical analysis of Epstein case developments with transparency warnings about government manipulation. Stay informed about document releases, court decisions, and investigative reporting."
        canonical="https://rtefn.com/news"
        keywords="Epstein case news, latest updates, developments, document releases, court decisions, investigative reporting, news coverage, case updates, breaking news"
        ogType="website"
        ogImage="https://rtefn.com/og-default.png"
        ogImageAlt="Epstein Case News Updates - Critical Analysis with Transparency Warnings"
      />
      <StructuredData data={[collectionPageSchema, ...newsSchemas]} />
      <Box
        style={{
          background:
            colorScheme === "dark"
              ? "linear-gradient(180deg, #0a0a0a 0%, #1a1a2e 100%)"
              : "linear-gradient(180deg, #f8fafc 0%, #e2e8f0 100%)",
          minHeight: "100vh",
        }}
      >
        <Container size="lg" py="xl">
          <Stack gap="xl">
            {/* Page Header */}
            <div className="text-center">
              <Group justify="center" gap="sm" mb="md">
                <IconNews size={48} color={colorScheme === "dark" ? "#3b82f6" : "#1e40af"} />
              </Group>
              <Title
                order={1}
                size="3rem"
                fw={700}
                ta="center"
                mb="md"
                style={{
                  color: colorScheme === "dark" ? "#ffffff" : "#1a1a1a",
                  letterSpacing: "-0.02em",
                }}
              >
                Latest News & Updates
              </Title>
              <Text
                size="xl"
                ta="center"
                style={{
                  color: colorScheme === "dark" ? "rgba(255, 255, 255, 0.8)" : "#64748b",
                  maxWidth: "600px",
                  margin: "0 auto",
                  lineHeight: 1.6,
                }}
              >
                Critical analysis of Epstein case developments with transparency warnings about government manipulation
              </Text>
            </div>

            {/* Transparency Alert */}
            <Alert
              icon={<IconAlertTriangle size={20} />}
              title="GOVERNMENT MANIPULATION ALERT"
              color="red"
              variant="filled"
            >
              <Text size="sm">
                ⚠️ Be aware that recent government "releases" are heavily manipulated. We provide critical analysis to
                expose administration interference and selective disclosure designed to protect powerful figures.
              </Text>
            </Alert>

            {/* Filters */}
            <Paper p="lg" radius="md">
              <Stack gap="md">
                {/* Transparency Level Tabs */}
                <Tabs value={activeTab} onChange={(value) => setActiveTab(value || "all")}>
                  <Tabs.List>
                    <Tabs.Tab value="all">All News</Tabs.Tab>
                    <Tabs.Tab value="authentic" color="green">
                      Authentic Sources
                    </Tabs.Tab>
                    <Tabs.Tab value="manipulated" color="red">
                      Government Influenced
                    </Tabs.Tab>
                  </Tabs.List>
                </Tabs>

                {/* Search and Category Filter */}
                <Group grow>
                  <TextInput
                    placeholder="Search articles..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.currentTarget.value)}
                    leftSection={<IconSearch size={16} />}
                  />
                  <Select
                    placeholder="Filter by category"
                    value={categoryFilter}
                    onChange={(value) => setCategoryFilter(value || "all")}
                    data={[
                      { value: "all", label: "All Categories" },
                      { value: "document-release", label: "Document Releases" },
                      { value: "congressional-action", label: "Congressional Action" },
                      { value: "survivor-testimony", label: "Survivor Testimony" },
                      { value: "administration-interference", label: "Administration Interference" },
                      { value: "legal-developments", label: "Legal Developments" },
                      { value: "media-coverage", label: "Media Coverage" },
                    ]}
                  />
                </Group>
              </Stack>
            </Paper>

            {/* News Articles */}
            <Stack gap="xl">
              {filteredArticles.length === 0 ? (
                <Paper p="xl" radius="lg" className="text-center">
                  <Stack gap="md">
                    <IconSearch size={48} className="mx-auto text-gray-400" />
                    <Title order={3} style={{ color: colorScheme === "dark" ? "#d1d5db" : "#4b5563" }}>
                      No articles found
                    </Title>
                    <Text style={{ color: colorScheme === "dark" ? "#9ca3af" : "#6b7280" }}>
                      Try adjusting your search terms or filters to see more results.
                    </Text>
                  </Stack>
                </Paper>
              ) : (
                filteredArticles.map(renderArticle)
              )}
            </Stack>

            {/* Call to Action */}
            <PageCTASection
              showNews={false}
              contextualAction={{ href: "/documents", label: "View Documents", icon: IconFileText }}
            />
          </Stack>
        </Container>
      </Box>
    </>
  )
}
